import { doc, runTransaction, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../../firebase";
import { hasApprovedWorkerReadiness } from "../../core/workforce/workerReadiness";

const revokedStatuses = ["inactive", "disabled", "suspended", "archived", "revoked"];
export function workerAccountRevoked(data = {}) {
  return data.active === false || data.disabled === true || data.archived === true || revokedStatuses.includes(data.status);
}

function requireWorker(user, access) {
  if (String(user.platformRole || user.role).toLowerCase() !== "worker" || !user.tenantId ||
      (access && (String(access.role).toLowerCase() !== "worker" || access.tenantId !== user.tenantId))) {
    throw new Error("A Worker with matching canonical tenant authority is required.");
  }
}

function canonicalFacilities(access) {
  const ids = access.facilityIds;
  if (!Array.isArray(ids) || !ids.length || ids.some(id => typeof id !== "string" || !id || id === "all") ||
      new Set(ids).size !== ids.length || !ids.includes(access.facilityId) || access.primaryFacilityId !== access.facilityId ||
      !Array.isArray(access.allowedFacilityIds) || access.allowedFacilityIds.length !== ids.length ||
      !access.allowedFacilityIds.every(id => ids.includes(id))) {
    throw new Error("Current canonical facility scope must be valid before reactivation.");
  }
  return ids;
}

// Preserve identity, scope and historical shifts. Never create a replacement access profile.
export async function disableWorkerAccount(uid, updates = {}) {
  return runTransaction(db, async tx => {
    const userRef = doc(db, "users", uid), accessRef = doc(db, "accessProfiles", uid);
    const user = await tx.get(userRef), access = await tx.get(accessRef);
    if (!user.exists()) throw new Error("Worker identity is missing.");
    requireWorker(user.data(), access.exists() ? access.data() : null);
    const audit = { updatedAt: serverTimestamp(), disabledAt: serverTimestamp(), disabledBy: auth.currentUser?.uid || "" };
    tx.update(userRef, { ...updates, status: revokedStatuses.includes(updates.status) ? updates.status : "disabled", active: false, disabled: true, ...audit });
    if (access.exists()) tx.update(accessRef, { active: false, disabled: true, status: "revoked", ...audit });
  });
}

// An explicit administrative operation: current approval and every retained parent are re-read.
export async function restoreWorkerAccount(uid) {
  return runTransaction(db, async tx => {
    const userRef = doc(db, "users", uid), accessRef = doc(db, "accessProfiles", uid);
    const user = await tx.get(userRef), access = await tx.get(accessRef);
    if (!user.exists() || !access.exists()) throw new Error("Existing Worker identity and access profile are required.");
    const identity = user.data(), authority = access.data();
    requireWorker(identity, authority);
    if (identity.archived === true || authority.archived === true) throw new Error("Archived Workers require administrative review.");
    if (!hasApprovedWorkerReadiness({ ...identity, status: "active", active: true, disabled: false })) {
      throw new Error("Current onboarding and compliance approval are required before reactivation.");
    }
    for (const facilityId of canonicalFacilities(authority)) {
      const parent = await tx.get(doc(db, "facilityProfiles", facilityId));
      if (!parent.exists() || parent.data().tenantId !== identity.tenantId) throw new Error("Facility tenant authority is missing or invalid.");
    }
    const enabled = { status: "active", active: true, disabled: false, enabledAt: serverTimestamp(), enabledBy: auth.currentUser?.uid || "", updatedAt: serverTimestamp() };
    tx.update(userRef, enabled);
    tx.update(accessRef, enabled);
  });
}

// Remove only this facility. Keep the roster row as an inactive historical record.
export async function removeWorkerFacility(uid, facilityId) {
  return runTransaction(db, async tx => {
    const userRef = doc(db, "users", uid), accessRef = doc(db, "accessProfiles", uid);
    const memberRef = doc(db, "facilityProfiles", facilityId, "workers", uid);
    const user = await tx.get(userRef), access = await tx.get(accessRef);
    const parent = await tx.get(doc(db, "facilityProfiles", facilityId)), member = await tx.get(memberRef);
    if (!user.exists() || !access.exists() || !parent.exists()) throw new Error("Existing Worker and facility authority are required.");
    requireWorker(user.data(), access.data());
    if (parent.data().tenantId !== user.data().tenantId) throw new Error("Facility tenant does not match Worker authority.");
    const ids = canonicalFacilities(access.data()).filter(id => id !== facilityId);
    const primary = ids.includes(access.data().primaryFacilityId) ? access.data().primaryFacilityId : ids[0] || "";
    const scope = { facilityId: primary, primaryFacilityId: primary, facilityIds: ids, allowedFacilityIds: ids, assignedFacilityIds: ids, updatedAt: serverTimestamp() };
    tx.update(userRef, scope);
    tx.update(accessRef, scope);
    if (member.exists()) tx.update(memberRef, { status: "inactive", active: false, disabled: true, disabledAt: serverTimestamp(), disabledBy: auth.currentUser?.uid || "", updatedAt: serverTimestamp() });
  });
}
